import env from '../../../../../core/config/env'
import { Box, Stack, Typography } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import AlertDefault from '../../../../../components/Alerts'
import LoadingBackdrop from '../../../../../components/Loading/LoadingBackdrop'
import DataTableHeaderMobile from '../../../../../components/Tables/DataTableHeaderMobile'
import SuccessSnackbar from '../../../../../components/Snackbar/SuccessSnackbar'
import ErrorSnackbar from '../../../../../components/Snackbar/ErrorSnackbar'
import DataTable, {
  IDataTableColumn,
} from '../../../../../components/Tables/DataTable'
import DataTableHeaderWeb from '../../../../../components/Tables/DataTableHeaderWeb'
import { TProductTicketLog } from '../../../../../core/types/Tickets'
import DataTableCellTypography from '../../../../../components/Typographys/DataTableCellTypography'
import DataTableSubcellTypography from '../../../../../components/Typographys/DataTableSubcellTypography'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import { useProductTicketLogs } from '../../../../../core/hooks/useProductTicketLogs'

const ProductTicketLogs = ({
  productId,
}: {
  productId?: string
}): JSX.Element => {
  const CHECKIN_BASE_URL = env.TICKET_BASE_URL
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [search, setSearch] = useState('')
  const oldSearch = useRef('')

  const { loading, data, error, execute, clear } = useProductTicketLogs({
    productId: productId ?? '',
    page,
    rows: rowsPerPage,
    search,
  })
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [errorSending, setErrorSending] = useState('')
  const [message, setMessage] = useState('')
  const [selectedTicket, setSelectedTicket] = useState<
    TProductTicketLog | undefined
  >()
  const columns: readonly IDataTableColumn<TProductTicketLog>[] = [
    {
      id: 'name',
      label: 'Quem',
      width: 120,
      render(id, value, row) {
        return (
          <Stack direction="column" width="120px">
            <DataTableCellTypography>{value}</DataTableCellTypography>
            <DataTableSubcellTypography noWrap={true}>
              {row.email}
            </DataTableSubcellTypography>
          </Stack>
        )
      },
    },
    {
      id: 'createdDate',
      label: 'Quando',
      width: 120,
      render(id, value, row) {
        return (
          <Stack direction="column" width="120px">
            <DataTableCellTypography>{value}</DataTableCellTypography>
            <DataTableSubcellTypography noWrap={true}>
              {row.email}
            </DataTableSubcellTypography>
          </Stack>
        )
      },
    },
    {
      id: 'oldPrice',
      label: 'De → Para',
      width: 120,
      render: (_, __, row) =>
        `R$ ${NumberFunctions.formatMoney(row.oldPrice)} → R$ ${NumberFunctions.formatMoney(row.newPrice)}`,
    },
  ]

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (rows: number) => {
    if (rows > 0) {
      setRowsPerPage(rows)
    }
    setPage(0)
  }

  useEffect(() => {
    if (
      data.rowsPerPage &&
      data.rowsPerPage > 0 &&
      data.rowsPerPage !== rowsPerPage
    ) {
      setRowsPerPage(data.rowsPerPage)
    }

    if (oldSearch.current !== search) {
      oldSearch.current = search
      setPage(0)
    }
  }, [data])

  return (
    <Box width="100%" sx={{ p: 3 }}>
      {error !== '' && (
        <Box sx={{ mb: 2 }}>
          <AlertDefault onClose={clear} severity="error" sx={{ width: '100%' }}>
            {error}
          </AlertDefault>
        </Box>
      )}

      <Stack spacing={2} width="100%">
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems={{ xs: 'start', sm: 'center' }}
          justifyContent={{ xs: 'start', sm: 'space-between' }}
          width="100%"
        >
          <Typography
            color="#343948"
            fontWeight={700}
            fontSize="16px"
            lineHeight="20px"
          >
            Histórico de mudanças de preço
          </Typography>
        </Stack>

        <Stack direction="column" spacing={1} sx={{ pt: 2 }}>
          <DataTable
            columns={columns}
            data={data}
            page={page}
            rowsPerPage={rowsPerPage}
            headerBackgroundColor="white"
            headerComponent={
              <DataTableHeaderWeb
                labelItems="log(s)"
                totalItems={data.total}
                setSearch={setSearch}
                executeSearch={execute}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            }
            headerMobileComponent={
              <DataTableHeaderMobile
                totalItems={data.total}
                setSearch={setSearch}
                executeSearch={execute}
                rowsPerPage={rowsPerPage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
              />
            }
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            hasNewCustomHeader
          />
        </Stack>
      </Stack>

      <LoadingBackdrop open={loading || sending} />

      <SuccessSnackbar open={message !== ''} onClose={() => setMessage('')}>
        {message}
      </SuccessSnackbar>

      <ErrorSnackbar
        open={errorSending !== ''}
        onClose={() => setErrorSending('')}
      >
        {errorSending}
      </ErrorSnackbar>

      <SuccessSnackbar open={success} onClose={() => setSuccess(false)}>
        Alterado com sucesso
      </SuccessSnackbar>
    </Box>
  )
}

export default ProductTicketLogs
